// Instants and geometry of the "Workday correction" timeline (story 202: RN82-RN84, S104-S106, S23).
// Pure functions, no React. Instants travel as ISO-8601 UTC text and live in the screen as epoch milliseconds
// (a number) or as `Date`; nothing below the millisecond is ever compared.

/** The instant truncated to the second (the screen sends `from`/`to` without a fraction of a second). */
export const truncateToSecond = (instant) => new Date(Math.floor(new Date(instant).getTime() / 1000) * 1000)

/**
 * IsoInstant.format (RN82): ISO-8601 UTC with `Z`, milliseconds only when there are some.
 * `2026-09-28T13:40:02.000Z` -> `2026-09-28T13:40:02Z`; `…02.400Z` stays.
 */
export const formatIsoInstant = (instant) => new Date(instant).toISOString().replace('.000Z', 'Z')

const ISO_WITH_FRACTION = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})(?:\.(\d{1,9}))?Z$/

/**
 * IsoInstant.parse (RN83, A202-6): Sun writes `ISO_INSTANT`, with 0 to 9 decimals. They are truncated to the
 * millisecond (never rounded, never compared below it). An unreadable text gives an invalid `Date` (NaN time).
 */
export const parseIsoInstant = (text) => {
  const match = ISO_WITH_FRACTION.exec(String(text))
  if (!match) return new Date(String(text))
  const milliseconds = ((match[2] || '') + '000').slice(0, 3)
  return new Date(`${match[1]}.${milliseconds}Z`)
}

const MIN_SEGMENT_WIDTH_PX = 2

/**
 * Where a segment goes (RN24): `left = (start - from) * scale`, never narrower than 2 px. The scale is px per
 * millisecond, so the position is by INSTANT: a day with a clock change simply spans 23 or 25 hours (RN84).
 */
export const segmentBox = ({ startMs, endMs, fromMs, pxPerMs }) => ({
  left: (startMs - fromMs) * pxPerMs,
  width: Math.max(MIN_SEGMENT_WIDTH_PX, (endMs - startMs) * pxPerMs)
})

// Local clock hours (0, 6, 12, 18 of each local day) inside [fromMs, toMs), in order. Built from the local calendar
// (not by adding 24 h), so a 23 h or 25 h day still starts at its local midnight (RN32, RN84).
const localMarks = (fromMs, toMs, hours) => {
  const marks = []
  const day = new Date(fromMs)
  day.setHours(0, 0, 0, 0)
  while (day.getTime() < toMs) {
    hours.forEach((hour) => {
      const mark = new Date(day.getFullYear(), day.getMonth(), day.getDate(), hour).getTime()
      if (mark >= fromMs && mark < toMs) marks.push(mark)
    })
    day.setDate(day.getDate() + 1)
  }
  return marks
}

/** The local midnights inside [fromMs, toMs): where the day labels of the axis go. */
export const localDayStarts = (fromMs, toMs) => localMarks(fromMs, toMs, [0])

/** The local 00:00, 06:00, 12:00 and 18:00 inside [fromMs, toMs): the ticks of the axis and its grid. */
export const sixHourMarks = (fromMs, toMs) => localMarks(fromMs, toMs, [0, 6, 12, 18])

export const DAY_MS = 24 * 3600 * 1000

/** Width of the sticky label column of the timeline (maqueta `.cell-l`). */
export const LABEL_COLUMN_WIDTH = 200

/** What the viewport measures when the browser cannot say (no layout, as in jsdom). */
export const DEFAULT_VIEWPORT_WIDTH = 1200

/** The day label of the axis: weekday, day and month in the language of the interface (RN32). */
export const DAY_LABEL_OPTIONS = { weekday: 'short', day: 'numeric', month: 'short' }

const INTL_LOCALE_BY_APP_LANGUAGE = { eus: 'eu' }

/** The language code the app uses -> the BCP 47 tag `Intl` understands. */
export const intlLocaleOf = (appLanguage) => INTL_LOCALE_BY_APP_LANGUAGE[appLanguage] || appLanguage

/**
 * The scale of the track in px per millisecond (RN12). The zoom is the number of days that fit in the visible width of
 * the track; when the whole range fits, it takes the whole width.
 */
export const pxPerMsFor = ({ zoomDays, trackViewportWidth, rangeMs }) => {
  const visibleMs = Math.min(zoomDays * DAY_MS, rangeMs)
  return trackViewportWidth / visibleMs
}

/** The instant at the centre of the visible track, given how far the container is scrolled (RN12). */
export const centerInstantOf = ({ scrollLeft, trackViewportWidth, pxPerMs, fromMs }) => fromMs + (scrollLeft + trackViewportWidth / 2) / pxPerMs

/** How far to scroll so that `centerMs` is at the centre of the visible track. */
export const scrollLeftFor = ({ centerMs, trackViewportWidth, pxPerMs, fromMs }) => Math.max(0, (centerMs - fromMs) * pxPerMs - trackViewportWidth / 2)
